"use client"

import { useEffect } from "react"

/**
 * Après un déploiement, un onglet resté ouvert référence d'anciens chunks JS
 * (hashés) qui n'existent plus -> ChunkLoadError / "Loading chunk failed" ->
 * écran bloqué jusqu'à un refresh manuel. Ce garde-fou détecte ce cas précis
 * et recharge la page automatiquement pour récupérer la nouvelle version.
 *
 * Garde anti-boucle : on ne recharge qu'une fois par fenêtre de 10 s
 * (sessionStorage). Si après reload l'erreur persiste (vrai bug réseau, pas un
 * déploiement), on n'entre pas en boucle infinie de rechargements.
 */
const RELOAD_KEY = "chunk-reload-last"
const RELOAD_WINDOW_MS = 10_000

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|error loading dynamically imported module|importing a module script failed|Failed to fetch dynamically imported module/i

function isChunkError(message: unknown): boolean {
  return typeof message === "string" && CHUNK_ERROR_RE.test(message)
}

function maybeReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
    if (Date.now() - last < RELOAD_WINDOW_MS) return // déjà rechargé récemment
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // sessionStorage indispo (mode privé strict) : on recharge quand même une fois
  }
  window.location.reload()
}

export function ChunkReloadGuard() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (isChunkError(e?.message) || isChunkError((e?.error as Error | undefined)?.name)) {
        maybeReload()
      }
    }
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e?.reason
      if (isChunkError(reason?.message) || isChunkError(reason?.name) || isChunkError(reason)) {
        maybeReload()
      }
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onRejection)
    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onRejection)
    }
  }, [])

  return null
}
